// <dropship-fee-sync>
//
// Executes the fee-line corrections computed server-side in
// snippets/embark-dropship-fee-sync.liquid: standalone dropship-fee lines
// (native bundle parents; `_dropship_bundle_*` properties) mirror their parent
// bundle line's quantity, and are removed when the parent is gone. The Cart
// Transform owns the fee pricing (per-unit upcharge), and this component never
// adds lines — only the PDP knows the fee, so a failed fee add simply leaves
// the bundle feeless.
//
// Runs from connectedCallback, i.e. on every cart re-render (section swaps
// re-instantiate the element) — the same reconcile-on-connect shape as
// <shipping-protection>. Corrections address lines by KEY: every fee line
// shares one dummy variant id, so index/variant addressing is unsafe.

if (!customElements.get('dropship-fee-sync')) {
  let syncInFlight = false;
  // Raw data-updates string of the last correction set written. An identical
  // consecutive set means the write didn't take (or a render raced) — skipping
  // it prevents a write loop. A clean render (no drift) re-arms the guard, so
  // only back-to-back identical failures are suppressed.
  let lastSyncedUpdates = null;

  class DropshipFeeSync extends HTMLElement {
    connectedCallback() {
      this.maybeSync();
    }

    async maybeSync() {
      const raw = this.dataset.updates;
      if (!raw) {
        lastSyncedUpdates = null;
        return;
      }
      if (syncInFlight || raw === lastSyncedUpdates) return;

      let updates;
      try {
        updates = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!Array.isArray(updates) || !updates.length) return;

      syncInFlight = true;
      DropshipFeeSync.dispatchCartChange('start');
      try {
        for (const update of updates) {
          // /cart/change.js takes one line per call; typically a single entry.
          // eslint-disable-next-line no-await-in-loop
          await DropshipFeeSync.ajaxCart('/cart/change.js', {
            id: update.key,
            quantity: update.quantity
          });
        }
        lastSyncedUpdates = raw;
        await DropshipFeeSync.refreshCart();
      } catch (err) {
        console.error('[dropship-fee-sync]', err); // eslint-disable-line
      } finally {
        syncInFlight = false;
        DropshipFeeSync.dispatchCartChange('end');
      }
    }

    static async refreshCart() {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh();
        return;
      }
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
      }
    }

    static dispatchCartChange(phase) {
      document.dispatchEvent(new CustomEvent(`on:cart:change:${phase}`, {
        bubbles: true,
        detail: { source: 'dropship-fee-sync' }
      }));
    }

    static async ajaxCart(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`${url} → ${response.status}: ${text}`);
      }

      return response.json();
    }
  }

  customElements.define('dropship-fee-sync', DropshipFeeSync);
}
